import deepEqual from 'fast-deep-equal'
import { autorun, computed, makeAutoObservable, reaction, toJS } from 'mobx'
import { FilterValues } from '../../packages/blueprint-components/src/useFilter'
import { InnerPagination, Params } from '../../packages/blueprint-components/src/useTableHooks'
import { UserStore } from '../../stores/user_store'
import { type EntityId, type SelectionI, type SelectionSet, idKey } from '../../types/constants'
import { AgGridColumnDef } from './AgGridTable'
import { resolveDefaultHiddenColumns, resolveDefaultPinning } from './resolve_defaults'
import { removeIncompleteSelectComponentFilters } from '../../util/transformSelectComponentFilters'

const normalisedKeys = (values: Set<EntityId>): Set<string> => {
    const keys = new Set<string>()
    values.forEach((value) => keys.add(idKey(value)))
    return keys
}

/**
 * The comparison key for a row, or null when the row carries no id under the
 * select key. A missing id must never normalise to the string "undefined" —
 * every such row would share one key and select or deselect together.
 */
const rowKey = (value: unknown): string | null =>
    value === null || value === undefined ? null : idKey(value)

/** Keep the persistence key aligned with the Columns menu's field/name fallback. */
export const columnKey = <T>(column: AgGridColumnDef<T> & { name?: string }): string =>
    (column.field ?? column.name) as string

/** Removes the entry matching `value`, whichever shape it was stored under. */
const deleteKey = (set: Set<EntityId>, value: EntityId) => {
    if (set.delete(value)) {
        return
    }

    const key = idKey(value)
    const stale: EntityId[] = []
    set.forEach((stored) => {
        if (idKey(stored) === key) {
            stale.push(stored)
        }
    })
    stale.forEach((stored) => set.delete(stored))
}

export class TableStore<T> {
    userStore: UserStore
    tableName: string
    items: T[] = []
    loading = false
    params: Params = {}
    totals: any = {}
    pagination: InnerPagination = {
        current: null,
        last_page: null,
        from: null,
        to: null,
        total: null,
    }
    selection: SelectionSet = {
        all: false,
        safeList: new Set(),
        blockList: new Set(),
        selectedCount: 0,
    }
    /** Raw, durable column overrides. Never prune these based on today's columns. */
    visibility: Record<string, boolean> = {}
    columnOrder: string[] = []
    columnWidths: Record<string, number> = {}
    pinning: Record<string, 'start' | 'end' | false> = {}
    private currentColumns: AgGridColumnDef<T>[] = []
    private hydratedProfileId: EntityId | null = null

    constructor(userStore: UserStore, tableName: string, defaultParams?: Params) {
        this.userStore = userStore
        this.tableName = tableName

        const paramsFromLocalStorage = this.paramsFromLocalStorage
        if (paramsFromLocalStorage) {
            this.params = paramsFromLocalStorage
        } else {
            this.params = {
                ...(defaultParams ?? {}),
                query: undefined,
                perPage: defaultParams?.perPage || 15,
                page: 1,
            }
        }

        // @todo init column order here too
        // @todo init column widths here too?

        // keepAlive: `isSelected` runs from an AG Grid cell renderer, which is
        // outside a mobx reaction. Without it the computed is uncached there and
        // every rendered cell rebuilds the whole normalised set.
        makeAutoObservable(this, {
            safeKeys: computed({ keepAlive: true }),
            blockKeys: computed({ keepAlive: true }),
        })

        // Add reaction to observe changes in profileId
        reaction(
            () => this.userStore.profileId,
            (profileId) => {
                this.hydratedProfileId = null
                this.visibility = profileId === null ? {} : Visibility.load(this.tableName, profileId) ?? {}
                this.columnOrder = profileId === null ? [] : Ordering.load(this.tableName, profileId) ?? []
                this.columnWidths = profileId === null ? {} : Widths.load(this.tableName, profileId) ?? {}
                this.pinning = profileId === null ? {} : Pinning.load(this.tableName, profileId) ?? {}
                this.items = []
                const paramsFromStorage = this.paramsFromLocalStorage
                if (paramsFromStorage) {
                    this.params = paramsFromStorage
                } else {
                    // Reset to default params if no stored params found.
                    this.params = {
                        ...(defaultParams ?? {}),
                        query: undefined,
                        perPage: defaultParams?.perPage || 15,
                        page: 1,
                    }
                }
                this.hydratedProfileId = profileId
            },
            { fireImmediately: true }, // This ensures it runs once immediately
        )

        // Add autorun to save params to local storage whenever they change
        autorun(() => {
            const key = this.key()
            if (key && this.hydratedProfileId === this.userStore.profileId) {
                saveToLocalStorage(key, this.params)
            }
        })

        autorun(() => this.saveColumnState())
    }

    get paramsFromLocalStorage(): Params | null {
        const key = this.key()
        if (!key) {
            return null
        }

        return loadFromLocalStorage(key)
    }

    key(): string | null {
        return tableSettingsKey(this.tableName, this.userStore.profileId)
    }

    setParams(params: Params | ((currentParams: Params) => Params)) {
        const newParams = typeof params === 'function' ? params(this.params) : params
        if (!deepEqual(this.params, newParams)) {
            if (!deepEqual(this.params?.filters, newParams?.filters)) {
                this.deselectAll()
            }

            this.params = newParams
        }
    }

    setSort(sort: string | undefined) {
        if (this.params.sort !== sort) {
            this.params.sort = sort
            this.params.page = 1
        }
    }

    setPerPage(perPage: number) {
        if (this.params.perPage !== perPage) {
            this.params.perPage = perPage
            this.params.page = 1
        }
    }

    setPage(page: number) {
        this.params.page = page
    }

    setItems(items: T[] | ((currentItems: T[]) => T[])) {
        if (typeof items === 'function') {
            this.items = [...items(this.items)]
        } else {
            this.items = [...items]
        }
    }

    setLoading(loading: boolean) {
        this.loading = loading
    }

    setTotals(totals: any) {
        this.totals = totals
    }

    setPagination(pagination: InnerPagination) {
        this.pagination = pagination
    }

    setFilters(filters: FilterValues) {
        const sanitizedFilters = removeIncompleteSelectComponentFilters(filters) as FilterValues

        // console.log('setFilters', toJS(filters), toJS(this.params.filter))
        if (!deepEqual(this.params.filter, sanitizedFilters)) {
            this.params.filter = sanitizedFilters
            this.params.page = 1
            this.deselectAll()
        }
    }

    forceReload() {
        this.params = { ...this.params }
    }

    // MER-2802: the selection sets keep whatever shape the row carried, so the
    // outgoing payload is unchanged. Membership is answered through these
    // normalised mirrors, which tolerate a number id and a string id alike.
    get safeKeys(): Set<string> {
        return normalisedKeys(this.selection.safeList)
    }

    get blockKeys(): Set<string> {
        return normalisedKeys(this.selection.blockList)
    }

    get isSelected() {
        return (key: keyof T, item: T): boolean => {
            const id = rowKey(item[key])
            if (id === null) {
                return false
            }

            if (this.selection.all) {
                return !this.blockKeys.has(id)
            }

            return this.safeKeys.has(id)
        }
    }

    selectRow(key: keyof T, item: T, checked: boolean) {
        if (rowKey(item[key]) === null) {
            return
        }

        const id = item[key] as unknown as EntityId

        if (checked) {
            if (this.selection.all) {
                deleteKey(this.selection.blockList, id)
                this.selection.selectedCount++
            } else {
                this.selection.safeList.add(id)
                this.selection.selectedCount++
            }
        } else {
            if (this.selection.all) {
                this.selection.blockList.add(id)
                this.selection.selectedCount--
            } else {
                deleteKey(this.selection.safeList, id)
                this.selection.selectedCount--
            }
        }
        //
    }

    get allChecked() {
        return (primaryKey: keyof T): true | false | 'indeterminate' => {
            // Look for any easy cases first
            if (this.selection.all && this.selection.blockList.size === 0) {
                return true
            } else if (!this.selection.all && this.selection.safeList.size === 0) {
                return false
            }

            // Check to make sure that all of _this_ page is selected before returning true
            // Hoisted out of the loop: outside a mobx reaction a computed is not
            // cached, so reading it per row would rebuild the set every time.
            const blockKeys = this.blockKeys
            const safeKeys = this.safeKeys

            let allOnPage = true
            this.items
                // .filter((i) => (canSelect ? canSelect(i) : true))
                .forEach((item) => {
                    const id = rowKey(item[primaryKey])
                    if (id === null) {
                        return
                    }

                    if (this.selection.all) {
                        if (blockKeys.has(id)) {
                            allOnPage = false
                        }
                    } else {
                        if (!safeKeys.has(id)) {
                            allOnPage = false
                        }
                    }
                })

            if (allOnPage) {
                return true
            }

            return 'indeterminate'
        }
    }

    get itemsOnPage() {
        return this.pagination.to - this.pagination.from + 1
    }

    get totalItems() {
        return this.pagination.total
    }

    setSelection(selection: SelectionI | ((currentSelection: SelectionI) => SelectionI)) {
        const newSelection =
            typeof selection === 'function'
                ? selection({
                      safeList: Array.from(this.selection.safeList),
                      blockList: Array.from(this.selection.blockList),
                      all: this.selection.all,
                      selectedCount: this.selection.selectedCount,
                  })
                : selection

        if (!deepEqual(this.selection, newSelection)) {
            this.selection = {
                safeList: new Set(newSelection.safeList),
                blockList: new Set(newSelection.blockList),
                all: newSelection.all,
                selectedCount: newSelection.selectedCount,
            }
        }
    }

    deselectAll() {
        this.selection.safeList.clear()
        this.selection.blockList.clear()
        this.selection.all = false
        this.selection.selectedCount = 0
    }

    selectAll() {
        this.selection.safeList.clear()
        this.selection.blockList.clear()
        this.selection.all = true
        this.selection.selectedCount = this.totalItems
    }

    selectRowsFromCurrentPage(primaryKey: keyof T, checked: boolean) {
        const allChecked = this.allChecked(primaryKey)

        if (checked === false && allChecked !== 'indeterminate') {
            // If we are unchecking from a select all state
            this.deselectAll()
        } else {
            const ids = this.items
                .map((i) => i[primaryKey])
                .filter((id) => rowKey(id) !== null) as unknown as EntityId[]

            this.selection.blockList.clear()
            this.selection.all = false
            for (const id of ids) {
                this.selection.safeList.add(id)
            }
            this.selection.selectedCount = this.selection.safeList.size
        }
    }

    /**
     * The sole projection from durable overrides to the columns currently rendered.
     * UI consumers use this rather than interpreting raw state or table defaults.
     */
    columnState(columns: AgGridColumnDef<T>[] = this.currentColumns) {
        const fields = columns.map((column) => columnKey(column))
        const defaultHidden = resolveDefaultHiddenColumns(columns, this.userStore.isKdp, this.tableName)
        columns.forEach((column) => {
            if (column.hide) {
                defaultHidden.add(columnKey(column))
            }
        })
        const defaultPinning = resolveDefaultPinning(columns, this.userStore.isKdp, this.tableName)
        const has = (value: object, key: string) => Object.prototype.hasOwnProperty.call(value, key)

        const hidden = new Set(
            fields.filter((field) =>
                has(this.visibility, field) ? !this.visibility[field] : defaultHidden.has(field),
            ),
        )
        const pinning: Record<string, 'start' | 'end' | false> = {}
        const widths: Record<string, number> = {}
        fields.forEach((field) => {
            if (has(this.pinning, field)) {
                pinning[field] = this.pinning[field]
            } else if (has(defaultPinning, field)) {
                pinning[field] = defaultPinning[field]
            }
            if (has(this.columnWidths, field)) {
                widths[field] = this.columnWidths[field]
            }
        })

        const fieldSet = new Set(fields)
        const order = this.columnOrder.filter((field, index, all) => fieldSet.has(field) && all.indexOf(field) === index)
        fields.forEach((field) => {
            if (!order.includes(field)) order.push(field)
        })
        return { hidden, pinning, widths, order }
    }

    isHidden(columnName: string) {
        return this.columnState().hidden.has(columnName)
    }

    isUserHidden(columnName: string) {
        const state = this.columnState()
        return (
            state.hidden.has(columnName) &&
            this.visibility[columnName] === false &&
            !resolveDefaultHiddenColumns(
                this.currentColumns,
                this.userStore.isKdp,
                this.tableName,
            ).has(columnName) &&
            !this.currentColumns.some((column) => columnKey(column) === columnName && column.hide)
        )
    }

    get userHiddenColumns(): string[] {
        return this.currentColumns.map((column) => columnKey(column)).filter((field) => this.isUserHidden(field))
    }

    get canRestoreColumns(): boolean {
        return this.userHiddenColumns.length > 0
    }

    toggleVisible(columnName: string) {
        // Store an explicit override for the inverse of the effective current value.
        this.visibility = { ...this.visibility, [columnName]: this.isHidden(columnName) }
    }

    restoreHiddenColumns(columns: AgGridColumnDef<T>[] = this.currentColumns) {
        const inScope = new Set(columns.map((column) => columnKey(column)))
        const restored = this.userHiddenColumns.filter((field) => inScope.has(field))
        if (!restored.length) {
            return
        }

        const visibility = { ...this.visibility }
        restored.forEach((field) => delete visibility[field])
        this.visibility = visibility
    }

    agGridColumnsWithVisibility(columns: AgGridColumnDef<T>[]) {
        this.currentColumns = columns
        const state = this.columnState(columns)
        const cols = columns.map((c) => {
            let col = { ...c }
            const key = columnKey(col)

            col.hide = state.hidden.has(key)

            if (Object.prototype.hasOwnProperty.call(state.widths, key)) {
                col.width = state.widths[key]
                // AG Grid prioritizes flex sizing over width. A manual resize is
                // an explicit switch from the responsive default to a fixed width.
                col.flex = undefined
            }

            if (state.pinning[key]) {
                const pinned = state.pinning[key]
                col.pinned = pinned === 'start' ? 'left' : 'right'
            } else {
                // https://www.ag-grid.com/react-data-grid/column-updating-definitions/#null-vs-undefined
                // The pinned column has to be explitly set to null, otherwise it won't clear
                col.pinned = null
            }

            return col
        })

        // Create a map of columns keyed by their field
        const colMap = new Map(cols.map((col) => [columnKey(col), col]))

        // Use the column order to create the final array of columns
        const orderedCols = state.order
            .map((field: string) => colMap.get(field as keyof T))
            .filter((col) => col !== undefined)

        // Add any columns that weren't in the order
        cols.forEach((col) => {
            if (!orderedCols.includes(col)) {
                orderedCols.push(col)
            }
        })

        return orderedCols
    }

    setColumnOrder(columns: AgGridColumnDef<T>[], order: string[]) {
        const currentFields = new Set(columns.map((column) => columnKey(column)))
        const reorderedCurrent = order.filter(
            (field, index) => currentFields.has(field) && order.indexOf(field) === index,
        )
        const remainingCurrent = columns
            .map((column) => columnKey(column))
            .filter((field) => !reorderedCurrent.includes(field))
        const replacement = [...reorderedCurrent, ...remainingCurrent]
        let index = 0
        const durable = this.columnOrder.reduce<string[]>((next, field) => {
            if (!currentFields.has(field)) {
                next.push(field)
            } else if (index < replacement.length) {
                next.push(replacement[index++])
            }
            return next
        }, [])
        this.columnOrder = [...durable, ...replacement.slice(index)]
    }

    setColumnWidths(columnName: string, width: number) {
        this.columnWidths = { ...this.columnWidths, [columnName]: width }
    }

    get isPinned() {
        return (columnName: string): 'start' | 'end' | false => {
            return this.columnState().pinning[columnName] ?? false
        }
    }

    setPinning(columnName: string, pinned: 'start' | 'end' | false) {
        this.pinning = { ...this.pinning, [columnName]: pinned }
    }

    private saveColumnState() {
        const profileId = this.userStore.profileId
        if (profileId === null || this.hydratedProfileId !== profileId) return
        Visibility.save(this.tableName, profileId, this.visibility)
        Ordering.save(this.tableName, profileId, this.columnOrder)
        Widths.save(this.tableName, profileId, this.columnWidths)
        Pinning.save(this.tableName, profileId, this.pinning)
    }
}

namespace Pinning {
    export const load = (
        tableName: string,
        profileId: EntityId | null,
    ): Record<string, 'start' | 'end' | false> | null => {
        const key = 'column_pinned:' + tableName + ':' + profileId
        const value = localStorage.getItem(key)
        if (!value) {
            return null
        }

        return JSON.parse(value)
    }

    export const save = (
        tableName: string,
        profileId: EntityId | null,
        pinned: Record<string, 'start' | 'end' | false>,
    ) => {
        const key = 'column_pinned:' + tableName + ':' + profileId
        localStorage.setItem(key, JSON.stringify(pinned))
    }
}

namespace Visibility {
    export const load = (
        tableName: string,
        profileId: EntityId | null,
    ): Record<string, boolean> | null => {
        const key = 'column_visible:' + tableName + ':' + profileId
        const value = localStorage.getItem(key)
        if (!value) {
            return null
        }

        return JSON.parse(value)
    }

    export const save = (
        tableName: string,
        profileId: EntityId | null,
        visible: Record<string, boolean>,
    ) => {
        const key = 'column_visible:' + tableName + ':' + profileId
        localStorage.setItem(key, JSON.stringify(visible))
    }
}

namespace Widths {
    export const load = (
        tableName: string,
        profileId: EntityId | null,
    ): Record<string, number> | null => {
        const key = 'column_widths:' + tableName + ':' + profileId
        const value = localStorage.getItem(key)
        if (!value) {
            return null
        }

        return JSON.parse(value)
    }

    export const save = (
        tableName: string,
        profileId: EntityId | null,
        widths: Record<string, number>,
    ) => {
        const key = 'column_widths:' + tableName + ':' + profileId
        localStorage.setItem(key, JSON.stringify(widths))
    }
}

namespace Ordering {
    export const load = (tableName: string, profileId: EntityId | null): string[] | null => {
        const key = 'column_order:' + tableName + ':' + profileId
        const value = localStorage.getItem(key)
        if (!value) {
            return null
        }

        return JSON.parse(value)
    }

    export const save = (tableName: string, profileId: EntityId | null, order: string[]) => {
        const key = 'column_order:' + tableName + ':' + profileId
        localStorage.setItem(key, JSON.stringify(order))
    }
}

export const tableSettingsKey = (tableName: string, profileId: EntityId | null) => {
    if (!profileId) {
        return null
    }

    return 'table_settings:' + tableName + ':' + profileId
}

export const loadFromLocalStorage = (key: string): Params | null => {
    const value = localStorage.getItem(key)
    if (!value) {
        return null
    }

    const v = JSON.parse(value)
    v.filter = removeIncompleteSelectComponentFilters(v.filter)
    for (const key of ['from', 'to']) {
        if (Object.prototype.hasOwnProperty.call(v, key)) {
            const date = typeof v[key] === 'string' ? new Date(v[key]) : null
            if (date && !Number.isNaN(date.getTime())) {
                v[key] = date
            } else {
                delete v[key]
            }
        }
    }
    return v
}

const saveToLocalStorage = <T>(key: string, value: T) => {
    localStorage.setItem(key, JSON.stringify(toJS(value)))
}
